import React, { useEffect, useState } from "react";
import axios from "axios";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import { API_get_invite, API_accept_invite } from "../constants";
import { setAuthToken } from "../utilities/requests";

const STATE_LOADING = "loading";
const STATE_ACTIVE = "active";
const STATE_ACCEPTED = "accepted";
const STATE_EXPIRED = "expired";
const STATE_INVALIDATED = "invalidated";
const STATE_NOT_FOUND = "not_found";
const STATE_ERROR = "error";

function persistAuth(token, username) {
  const expirationDate = new Date(
    new Date().getTime() + 3600 * 100000 * 24 * 1
  );
  localStorage.setItem("token", token);
  localStorage.setItem("user", username);
  localStorage.setItem("email", username);
  localStorage.setItem("expirationDate", expirationDate);
  setAuthToken(token);
}

function WelcomeAccept({ token }) {
  const [pageState, setPageState] = useState(STATE_LOADING);
  const [invite, setInvite] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const inviteUsername = invite?.username || invite?.email;
  const isCompanyCreation = invite?.is_company_creation === true;
  const inviteDescription = isCompanyCreation
    ? `${invite?.company_name || "Your company"} is ready. Create your password to sign in and continue onboarding.`
    : invite?.company_name && invite?.store_name
    ? `${invite.company_name} added you as a team member at ${invite.store_name}. Set your password to get started.`
    : invite?.company_name
    ? `${invite.company_name} added you as a team member. Set your password to get started.`
    : invite?.store_name
    ? `${invite.store_name} added you as a team member. Set your password to get started.`
    : "You've been added as a team member. Set your password to get started.";

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await axios.get(API_get_invite + token);
        if (cancelled) return;
        setInvite(res.data);
        setPageState(STATE_ACTIVE);
      } catch (err) {
        if (cancelled) return;
        const data = err?.response?.data;
        if (err?.response?.status === 410) {
          setInvite(data || null);
          const status = data?.status;
          if (status === "accepted") {
            setPageState(STATE_ACCEPTED);
          } else if (status === "invalidated") {
            setPageState(STATE_INVALIDATED);
          } else {
            setPageState(STATE_EXPIRED);
          }
        } else if (err?.response?.status === 404) {
          setPageState(STATE_NOT_FOUND);
        } else {
          setPageState(STATE_ERROR);
        }
      }
    }
    if (token) {
      load();
    } else {
      setPageState(STATE_NOT_FOUND);
    }
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    setErrorMessage("");
    if (password.length < 8) {
      setErrorMessage("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(API_accept_invite, {
        token,
        password,
      });
      const authToken = res.data.auth_token;
      const username = res.data.username;
      const acceptedCompanyCreation = res.data.is_company_creation === true;
      // If a previous user was signed in on this device, drop their state
      // (Stream Chat token, expirationDate, etc.) before persisting the new
      // session. Otherwise the new user inherits stale localStorage keys.
      localStorage.clear();
      persistAuth(authToken, username);
      window.location.assign(
        acceptedCompanyCreation ? "/?companySetup=complete" : "/"
      );
    } catch (err) {
      const data = err?.response?.data;
      if (err?.response?.status === 410) {
        const status = data?.status;
        if (status === "accepted") {
          setPageState(STATE_ACCEPTED);
        } else if (status === "invalidated") {
          setPageState(STATE_INVALIDATED);
        } else {
          setPageState(STATE_EXPIRED);
        }
      } else if (err?.response?.status === 429) {
        setErrorMessage(
          data?.detail ||
            "Too many attempts. Please try again in a few minutes."
        );
      } else if (data?.detail) {
        setErrorMessage(data.detail);
      } else {
        setErrorMessage("Something went wrong. Please try again.");
      }
      setSubmitting(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f8fafc",
        padding: "24px",
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          maxWidth: 440,
          padding: "40px 32px",
          borderRadius: "16px",
        }}
      >
        {pageState === STATE_LOADING && (
          <Typography align="center" color="textSecondary">
            Loading…
          </Typography>
        )}

        {pageState === STATE_ACTIVE && invite && (
          <>
            {isCompanyCreation && (
              <Box sx={{ mb: 4 }}>
                <Box
                  sx={{
                    height: 4,
                    borderRadius: 999,
                    backgroundColor: "#e2e8f0",
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ width: "100%", height: "100%", backgroundColor: "#6d28d9" }} />
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                  <Typography variant="caption" color="textSecondary">Step 6 of 6</Typography>
                  <Typography variant="caption" color="textSecondary">Final step</Typography>
                </Box>
              </Box>
            )}
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              {isCompanyCreation ? "Finish setting up your account" : "Welcome to Rightwork"}
            </Typography>
            <Typography color="textSecondary" sx={{ mb: 3 }}>
              {invite.first_name ? `Hi ${invite.first_name}, ` : ""}
              {inviteDescription}
            </Typography>
            <form onSubmit={handleSubmit}>
              {inviteUsername && (
                <div>
                  <p className="text-slate-500 text-sm">Username</p>
                  <div className="mt-1.5">
                    <TextField
                      variant="outlined"
                      fullWidth
                      name="username"
                      id="username"
                      value={inviteUsername}
                      autoComplete="username"
                      inputProps={{
                        readOnly: true,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px !important",
                          minHeight: "55px",
                          backgroundColor: "#f8fafc",
                        },
                        "& .MuiInputBase-input": {
                          color: "#0f172a",
                          fontWeight: 600,
                          overflowWrap: "anywhere",
                        },
                      }}
                    />
                  </div>
                  <Typography color="textSecondary" variant="body2" sx={{ mt: 1, mb: 3 }}>
                    {isCompanyCreation
                      ? "You'll use this email to sign in to Rightwork."
                      : "You'll use this username to sign in from the mobile app."}
                  </Typography>
                </div>
              )}
              <div>
                <p className="text-slate-500 text-sm">Password</p>
                <div className="mt-1.5">
                  <TextField
                    variant="outlined"
                    required
                    fullWidth
                    name="password"
                    placeholder="Enter password"
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoFocus
                    autoComplete="new-password"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px !important",
                        height: "55px",
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6d28d9",
                        },
                        backgroundColor: "white",
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword((v) => !v)}
                          >
                            {showPassword ? (
                              <VisibilityIcon style={{ fontSize: "22px" }} />
                            ) : (
                              <VisibilityOffIcon style={{ fontSize: "22px" }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
              </div>
              <div className="mt-5">
                <p className="text-slate-500 text-sm">Confirm password</p>
                <div className="mt-1.5">
                  <TextField
                    variant="outlined"
                    required
                    fullWidth
                    name="confirm_password"
                    placeholder="Re-enter password"
                    type={showPassword ? "text" : "password"}
                    id="confirm_password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px !important",
                        height: "55px",
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6d28d9",
                        },
                        backgroundColor: "white",
                      },
                    }}
                  />
                </div>
              </div>
              {errorMessage && (
                <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                  {errorMessage}
                </Typography>
              )}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={submitting}
                sx={{
                  mt: 4,
                  py: 1.5,
                  borderRadius: "12px",
                  backgroundColor: "#6d28d9",
                  "&:hover": { backgroundColor: "#5b21b6" },
                  textTransform: "none",
                  fontSize: "16px",
                  fontWeight: 600,
                }}
              >
                {submitting
                  ? "Setting up your account…"
                  : isCompanyCreation
                  ? "Create password & enter Rightwork"
                  : "Set password & continue"}
              </Button>
            </form>
          </>
        )}

        {pageState === STATE_EXPIRED && (
          <>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              This invite has expired
            </Typography>
            <Typography color="textSecondary" sx={{ mb: 3 }}>
              Welcome links expire after a couple of weeks. Ask your manager
              to send a new invite, or sign in if you already have an account.
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              href="/"
              sx={{
                textTransform: "none",
                py: 1.25,
                borderColor: "#6d28d9",
                color: "#6d28d9",
                "&:hover": {
                  borderColor: "#5b21b6",
                  backgroundColor: "rgba(109, 40, 217, 0.04)",
                },
              }}
            >
              Go to sign in
            </Button>
          </>
        )}

        {pageState === STATE_INVALIDATED && (
          <>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              This link is no longer valid
            </Typography>
            <Typography color="textSecondary" sx={{ mb: 3 }}>
              A newer invite has been sent. Check your messages for the most
              recent welcome message, or ask your manager to resend.
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              href="/"
              sx={{
                textTransform: "none",
                py: 1.25,
                borderColor: "#6d28d9",
                color: "#6d28d9",
                "&:hover": {
                  borderColor: "#5b21b6",
                  backgroundColor: "rgba(109, 40, 217, 0.04)",
                },
              }}
            >
              Go to sign in
            </Button>
          </>
        )}

        {pageState === STATE_ACCEPTED && (
          <>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              You're all set
            </Typography>
            <Typography color="textSecondary" sx={{ mb: 3 }}>
              This invite has already been used. Sign in with your password to
              get back in.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              href="/"
              sx={{
                backgroundColor: "#6d28d9",
                "&:hover": { backgroundColor: "#5b21b6" },
                textTransform: "none",
                py: 1.25,
              }}
            >
              Go to sign in
            </Button>
          </>
        )}

        {pageState === STATE_NOT_FOUND && (
          <>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Invite not found
            </Typography>
            <Typography color="textSecondary" sx={{ mb: 3 }}>
              We couldn't find this invite. Check the link in your welcome
              message, or ask your manager to resend.
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              href="/"
              sx={{
                textTransform: "none",
                py: 1.25,
                borderColor: "#6d28d9",
                color: "#6d28d9",
                "&:hover": {
                  borderColor: "#5b21b6",
                  backgroundColor: "rgba(109, 40, 217, 0.04)",
                },
              }}
            >
              Go to sign in
            </Button>
          </>
        )}

        {pageState === STATE_ERROR && (
          <>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Something went wrong
            </Typography>
            <Typography color="textSecondary" sx={{ mb: 3 }}>
              We couldn't load your invite right now. Please try again in a
              few minutes.
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              href="/"
              sx={{
                textTransform: "none",
                py: 1.25,
                borderColor: "#6d28d9",
                color: "#6d28d9",
                "&:hover": {
                  borderColor: "#5b21b6",
                  backgroundColor: "rgba(109, 40, 217, 0.04)",
                },
              }}
            >
              Go to sign in
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
}

export default WelcomeAccept;
